const skillsGuide = {
  id: "skills-guide",
  title: "Skills",
  category: "Guides",
  slug: "skills",
  meta: {
    description: "A compilation of all of the Pixels Skills, all items crafted within those skills, how to level and how to use",
    keywords: [
      "skills",
      "guides",
      "farming",
      "mining",
      "metalworking",
      "forestry",
      "fishing",
      "animal care",
      "petcare",
      "stoneshaping",
      "woodworking",
      "business",
      "textile",
      "windmill",
      "exploration",
    ],
    author: "PixeLore",
    date: "2025-04-27",
  },
  sections: [
    {
      id: "breakdown",
      title: "Breakdown of All Skills",
      type: "section",
      content: [
        {
          type: "component",
          data: "Skills",
        },
      ],
    },
    {
      id: "general-info",
      title: "General Info About Skills",
      type: "section",
      content: [
        {
          title: "About Skills",
          id: "about-skills",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `There are currently 10 different Skills in the Pixels Game. We can break these down into 2 different categories:<br/><br/>
            ✨<strong>Resource Skills</strong> - These are the industries that you use and get an item back such as farming (you use seeds, and get back grown crops), or mining (you use a pick and you get back mined resources). You will also see these items used in the task board or in available recipes<br/>
            ✨<strong>Crafting Skills</strong> - These are industries where you can craft something, like cooking or stoneshaping. You take raw resources or other crafted items, and make a completely new item with them. You will see these items used in the Task Board or in events.`,
            },
            {
              type: "text",
              data: `Each skill requires the same amount of EXP to get to the higher levels, and each skill represents different industries, items or profit. Some skills might be better to focus on than others. Game metas change quite often and can sometimes make one skill more attractive than another`,
            },
            {
              type: "text",
              data: "Below is a breakdown of the skill, category, and relevant industries:",
            },
            {
              type: "table",
              data: [
                {
                  skill: "Farming",
                  category: "Resource",
                  "Related Industries": "Planting Crops, Windmill",
                },
                {
                  skill: "Cooking",
                  category: "Crafting",
                  "Related Industries": "Stoves, Grills",
                },
                {
                  skill: "Forestry",
                  category: "Resource",
                  "Related Industries": "Trees",
                },
                {
                  skill: "Woodworking",
                  category: "Crafting",
                  "Related Industries": "Woodworking Benches",
                },
                {
                  skill: "Mining",
                  category: "Resource",
                  "Related Industries": "Mines, Special Mines",
                },
                {
                  skill: "Metalworking",
                  category: "Crafting",
                  "Related Industries": "Metalworking Benches",
                },
                {
                  skill: "Stoneshaping",
                  category: "Crafting",
                  "Related Industries": "Stoneshaping Benches",
                },
                {
                  skill: "Animal Care",
                  category: "Resource",
                  "Related Industries": "Coops, Slug Hutches, Apiaries, BBQ Grills (pet treats), Composter",
                },
                {
                  skill: "Business",
                  category: "Crafting",
                  "Related Industries": "Winery, The Machine, Textile Mill",
                },
                {
                  skill: "Exploration",
                  category: "None",
                  "Related Industries": "Skill not in use",
                },
              ],
            },
          ],
        },
        {
          title: "Tiers",
          id: "tiers",
          type: "subdivision",
          content: [
            {
              type: "text",
              data: `<strong>Tiers</strong> are how skills are broken up and categorized. You will see that as you level up, you unlock higher level items that require higher tier tools.<br/>
            As an example, at the lower tiers, you can use basic tools such as the Basic Pick to mine, Basic Axe to cut trees, Basic Soil to plant, Basic Shears to harvest, etc.<br/>
            When you start to use higher tier industries, you will need the equivalent tier or higher tier tools.<br/>
            Every Tier 1 Kit & tool can be purchased at the store<br/>
            <u>Currently Tier 5 is useless.</u>`,
            },
            {
              type: "text",
              data: `Here is the breakdown of the Tiers and relevant industries/tools that you can expect to see in those tiers:`,
            },
            {
              type: "table",
              data: [
                {
                  tier: "1",
                  levels: "1-19",
                  industries: `Basic Mine<br/>
                        Basic Soil<br/>
                        Basic Metalworking Kit<br/>
                        Basic Stoneshaping Kit<br/>
                        Basic Woodworking Bench<br/>
                        Whittlewood Tree<br/>
                        Basic Stove`,
                  tools: `Basic Pick<br/>
                    Basic Shears<br/>
                    Basic Axe`,
                },
                {
                  tier: "2",
                  levels: "20-39",
                  industries: `Improved Mine<br/>
                      Enriched Soil<br/>
                      Improved Metalworking Kit<br/>
                      Improved Stoneshaping Kit<br/>
                      Improved Woodworking Bench<br/>
                      Craftbark Tree<br/>
                      Improved Stove`,
                  tools: `Bronzenite Pick<br/>
                      Bronzenite Shears<br/>
                      Bronzenite Axe`,
                },
                {
                  tier: "3",
                  levels: "40-59",
                  industries: `Refined Mine<br/>
                  Fortified Soil<br/>
                  Refined Metalworking Kit<br/>
                  Refined Stoneshaping Kit<br/>
                  Refined Woodworking Bench<br/>
                  Sawstump Tree<br/>
                  Refined Stove`,
                  tools: `Irontite Pick<br/>
                  Irontite Shears<br/>
                  Irontite Axe`,
                },
                {
                  tier: "4",
                  levels: "60-79",
                  industries: `	Master Mine<br/>
                    Lush Soil<br/>
                    Master Metalworking Kit<br/>
                    Master Stoneshaping Kit<br/>
                    Master Woodworking Bench<br/>
                    Borebranch Tree<br/>
                    Master Stove`,
                  tools: `Adamaxium Pick<br/>
                  Adamaxium Shears<br/>
                  Adamaxium Axe`,
                },
                {
                  tier: "5",
                  levels: "80-100",
                  industries: `Elite Mine<br/>
                      Verdant Soil<br/>
                      Elite Metalworking Kit<br/>
                      Elite Stoneshaping Kit<br/>
                      Elite Woodworking Bench<br/>
                      Hammeroot Tree<br/>
                      Elite Stove`,
                  tools: `Unobtainium Pick<br/>
                    Unobtainium Shears<br/>
                    Unobtainium Axe`,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "skill-info",
      title: "Detailed Info about Each Skill",
      type: "section",
      content: [
        {
          id: "farming",
          title: "Farming",
          type: "subsection",
          content: [
            {
              title: "Farming",
              id: "about-farming",
              type: "subdivision",
              content: [
                {
                  type: "text",
                  data: `Farming is one of the skills in Pixels that plays a major role in many other industries. It is a Resource Skill and is one of the core skills, imo - the resources you get from Farming can be used directly in the Task Board, can be used in Cooking, Grilling, making Wines and other drinks.`,
                },
                {
                  type: "text",
                  data: `
                    Here are some tips about farming that might help you as you start your farming journey:
                    <br/>🧑🏻‍🌾For every crop you plant, make sure you have the adequate tool to harvest. For example if you're planting a Tier 3 Crop, you need a Tier 3 Shears to Harvest the crop.
                    <br/>🧑🏻‍🌾The Rusty Watering Can has no tier.
                    <br/>🧑🏻‍🌾If you dont harvest within 4 hours of the crop being fully grown, your plant will die.
                    <br/>🧑🏻‍🌾Watering always costs 0.5 Energy
                    <br/>🧑🏻‍🌾There's no public farming areas, but there's a public windmill located top left of Terra Villa.
                    <br/>🧑🏻‍🌾You can also buy Tier 1 soils for 100 coins from the store, and place them on your Speck for easy beginner farming.
                    <br/>🧑🏻‍🌾Soils cannot be placed inside the house, only exterior on lands.`,
                },
                {
                  type: "text",
                  data: `<u><b>The New Update</b></u>
                  <br/>🌱 As of May 13th, you can now harvest other player's crops to clear the soil
                  <br/>⛓️‍💥 When you harvest a crop, it costs durability on your shears
                  <br/>⚡ It doesn't cost energy to harvest someone else's crop
                  <br/>🪢 You need the right tier of shears to harvest the crop that is planted even if it's not your crop!
                  <br/>⌚ The timer on your crop continues even if someone else sheared it, so you still need to collect in time
                  <br/><br/>🧑🏻‍🌾 When you collect your crop, it will give you the XP and cost you energy to pick them up
                  <br/>🧑🏻‍🌾 If your crop died, collecting the seed will cost energy but give no XP`
                },
                {
                  type: "inlineLink",
                  data: {
                    text: "Golden Rake",
                    itemId: "itm_goldenRake",
                    description: `<u><b>Special</b></u>
                    <br/>🧺 If you own a Golden Rake, you can harvest any tier of crop
                    <br/>🧺 The rake has no durability, so it will never die
                    <br/>🧺 It also has a 1% chance to return an extra crop when harvesting!`
                  }
                }
              ],
            },
          ],
        },
        {
          id: "cooking",
          title: "Cooking",
          type: "subsection",
          content: [
            {
              title: "Cooking",
              id: "about-cooking",
              type: "subdivision",
              content: [
                {
                  type: "text",
                  data: `Cooking is a Crafting Skill that leads you down the path of energy-rejuvenation. Some of the items made with cooking can be good for energy or strictly utilized in the task board. There's also a lot of items that you can make to craft other items, like jams or purees.`,
                },
                {
                  type: "text",
                  data: `
                   Here are some tips about cooking that might help you as you start your cooking journey:
                  <br/>👨🏻‍🍳You can use higher tier stoves even if you haven't reached that tier of cooking. This is super helpful because Tier 3 and Tier 4 stoves don't require wood.
                  <br/>👨🏻‍🍳If you're using a Tier 1 or Tier 2 stove, you need to place 1 wood first before you can use the stove
                  <br/>👨🏻‍🍳Wood lasts for 30 minutes before it dies out and then you need to apply more wood (if using stoves that require wood).
                  <br/>👨🏻‍🍳The grills are another place where you can raise your cooking skill - there are 3 grills in the game and their recipes are each different [Dry, Moist & Starry] - Can you guess which is which?`,
                },
                {
                  type: "text",
                  data: `👨🏻‍🍳There's no public cooking area but there is a public BarnBQ Grill located on the Beach.
                        <br/>👨🏻‍🍳You can also buy Tier 1 Stoves for 100 coins from the store, and place them inside your Speck's house for easy beginner cooking.
                        <br/>👨🏻‍🍳Stoves cannot be placed outside of a house.`,
                },
                {
                  type: "text",
                  data: `👨🏻‍🍳Different wood logs burn for different times in the T1 and T2 Stoves:<br/>
                    T1 Wood: 45 Seconds<br/>
                    T2 Wood: 3 Minutes and 20 Seconds<br/>
                    T3 Wood: 7 Minutes and 20 Seconds<br/>
                    T4 Wood: 13 Minutes and 20 Seconds<br/>
                    T5 Wood: Insta-ban (kidding but how did you get that?)`
                }
              ],
            },
          ],
        },
        {
          id: "forestry",
          title: "Forestry",
          type: "subsection",
          content: [
            {
              title: "Forestry",
              id: "about-forestry",
              type: "subdivision",
              content: [
                {
                  type: "text",
                  data: `Forestry is a Resource Skill that requires the use of an Axe to chop down trees. Drops from this skill can be useful in Woodworking and the Task Board.`,
                },
                {
                  type: "text",
                  data: `
                    Here are some tips about Forestry that might help you as you start your woodcutting journey:
                    <br/>🪓You cannot cut down trees higher than your tier level even if you have the right tool
                    <br/>🪓Every cut takes a different amount of Energy. Between 1 and 2. It averages out to 1.5 E per cut
                    <br/>🪓If you try to cut down a tier 3 tree with a tier 2 Axe, it wont work - but you can cut a Tier 2 Tree with a Tier 3 Tool if you are high enough level to use the T3 Tool
                    <br/>🪓Trees can only be cut in stump form and in fully grown form (Much different from before where there were 3 cutting phases.)
                    <br/>🪓Trees drop the resources after you've cut them fully.
                    <br/>🪓The amount of chops vary depending on RNG. Sometimes it only takes 1 hit to cut down a tree. Sometimes it takes 11. And that number can be anything in between. It should average out to 6 cuts over time.
                    <br/>🪓You only get EXP if you collect the drops from the trees. The drops hold the EXP, not the tree cuts itself`,
                },
                {
                  type: "text",
                  data: `🪓There are trees scattered around Terra Villa and the Carnival grounds that you can cut publicly
                          <br/>🪓You can also buy Tier 1 Trees for 100 coins from the store, and place them on your land for easy early-game leveling.
                          <br/>🪓Trees cannot be placed inside of a house.`,
                },
              ],
            },
          ],
        },
        {
          id: "woodworking",
          title: "Woodworking",
          type: "subsection",
          content: [
            {
              title: "Woodworking",
              id: "about-woodworking",
              type: "subdivision",
              content: [
                {
                  type: "text",
                  data: `Woodworking is a Crafting Skill that mostly uses the drops from Forestry to craft items. There's nothing particularly special about these items, but can be helpful for completing tasks on the Task Board. This is also the skill you want to level if you'd like to craft your own storage chests`,
                },
                {
                  type: "text",
                  data: `
                    Here are some tips about woodworking that might help you as you start your woodworking journey:
                  <br/>🪚You can use higher woodworking benches even if you haven't reached the appropriate tier. So there's no limitation on which bench you can use
                  <br/>🪚There's one public woodworking bench on the left side of the Hot Air Balloon (center Terra Villa)
                  <br/>🪚You can also buy Tier 1 Woodworking Benches for 100 coins from the store, and place them on your speck for easy beginner woodworking.
                  <br/>🪚Woodworking Benches can be placed inside and outside`,
                },
              ],
            },
          ],
        },
        {
          id: "mining",
          title: "Mining",
          type: "subsection",
          content: [
            {
              title: "Mining",
              id: "about-mining",
              type: "subdivision",
              content: [
                {
                  type: "text",
                  data: `Mining is a Resource Skill that requires the use of a Pick Axe to gather resources from various mines. Drops from this skill can be useful in Metalworking, Stoneshaping and the Task Board.`,
                },
                {
                  type: "text",
                  data: `
                    Here are some tips about Mining that might help you as you start your mining journey:
                    <br/>⛏️You cannot use Mines higher than your tier level even if you have the right tool
                    <br/>⛏️If you try to use a lower tier tool on a higher tier mine, it wont work - but you can use a higher tier tool on a lower tier Mine if you are high enough level to use the T3 Tool
                    <br/>⛏️Mining has a percentage chance of being successful. Every other time you'll see the Mine rejects your attempt. Keep trying until it has the 2 minute timer.
                    <br/>⛏️If you use a higher tier tool on a lower level mine, the chances are increased for success
                    <br/>⛏️You get EXP from a Mine when you collect the resources from the mine.

                    <br/><br/>⛏️There is no public mine
                    <br/>⛏️You can also buy Tier 1 Mines for 100 coins from the store, and place them on your land for easy early-game leveling.
                    <br/>⛏️Mines cannot be placed inside of a house.`,
                },
              ],
            },
          ],
        },
        {
          id: "stoneshaping",
          title: "Stoneshaping",
          type: "subsection",
          content: [
            {
              title: "Stoneshaping",
              id: "about-stoneshaping",
              type: "subdivision",
              content: [
                {
                  type: "text",
                  data: `Stoneshaping is a Crafting Skill that mostly uses the drops from Mining to craft items. There's nothing particularly special about these items, but they can be helpful for completing tasks on the Task Board.`,
                },
                {
                  type: "text",
                  data: `
                    Here are some tips about stoneshaping that might help you as you start your journey:
                    <br/>🪨You can use higher stoneshaping benches even if you haven't reached the appropriate tier. So there's no limitation on which bench you can use
                    <br/>🪨All benches require wood before they can operate. The wood lasts for 30 minutes

                    <br/><br/>🪨There's one public stoneshaping bench on the left side of the Pixel HQ (walk top left)
                    <br/>🪨You can also buy Tier 1 Stoneshaping Benches for 100 coins from the store, and place them on your Speck for easy beginner stoneshaping.
                    <br/>🪨Stoneshaping Benches can be placed inside and outside`,
                },
                {
                  type: "text",
                  data: `🪨Different wood logs burn for different times in the Stoneshaping Kiln:<br/>
                    T1 Wood: 45 Seconds<br/>
                    T2 Wood: 3 Minutes and 20 Seconds<br/>
                    T3 Wood: 7 Minutes and 20 Seconds<br/>
                    T4 Wood: 13 Minutes and 20 Seconds<br/>
                    T5 Wood: Insta-ban (kidding but how did you get that?)`
                }
              ],
            },
          ],
        },
        {
          id: "metalworking",
          title: "Metalworking",
          type: "subsection",
          content: [
            {
              title: "Metalworking",
              id: "about-metalworking",
              type: "subdivision",
              content: [
                {
                  type: "text",
                  data: `Metalworking is a Crafting Skill that mostly uses the drops from Mining to craft items. There's nothing particularly special about these items, but they can be helpful for completing tasks on the Task Board.`,
                },
                {
                  type: "text",
                  data: `
                    Here are some tips about metalworking that might help you as you start your journey:
                  <br/>🫙You can use higher metalworking benches even if you haven't reached the appropriate tier. So there's no limitation on which bench you can use

                  <br/><br/>🫙There's one public metalworking bench on the right side of the Pixel HQ (walk top right)
                  <br/>🫙You can also buy Tier 1 metalworking Benches for 100 coins from the store, and place them on your Speck for easy beginner metalworking.
                  <br/>🫙Metalworking Benches can be placed inside and outside`,
                },
              ],
            },
          ],
        },
        {
          id: "petcare",
          title: "Animal Care",
          type: "subsection",
          content: [
            {
              title: "Animal Care",
              id: "about-petcare",
              type: "subdivision",
              content: [
                {
                  type: "text",
                  data: `Animal Care can be a mix of crafting and resource industries. Every skill that has animals involved will most likely increase this ability's exp. So think bees, slugs, chickens, and even crafting pet treats
                  <br/>Since the update to Animal Care, I've made a full page highlighting all the Animal Care things. Take a look at <a href="/wiki/animalcare" target="_blank" rel="noreferrer" class="wiki-links">this page.</a>`,
                },
              ],
            },
          ],
        },
        {
          id: "business",
          title: "Business",
          type: "subsection",
          content: [
            {
              title: "Business",
              id: "about-business",
              type: "subdivision",
              content: [
                {
                  type: "text",
                  data: `Business has a mixture of Crafting Industries
                    <br/>⚙️The Machine located inside of the Ministry of Innovation (bottom left of the Fountain) has a couple of the recipes for Business.
                    <br/>🍷The Winery located at the bottom left of the map is also a source of recipes for the Business Skill. You can only use bulk recipes if you're VIP
                    <br/>🍷 For higher tiered winery recipes, you need to use NFT Lands`,
                },
                {
                  type: "text",
                  data: `
                    🕹️There is also a non-Pixels way to raise those Business Skills via NuCyber. Nucyber's manufacturing and packaging abilities grant you Business EXP which translates to Pixels Exp`,
                },
                {
                  type: "text",
                  data: `NuCyber is a game built on top of the Pixels Engine, but does not really relate directly to Pixels or its game loop. It's an outside game that you can choose to play or not. The original game was created by Llamaverse creator but when the creator spent all the funds in a gambling session, he had to sell and the product was eventually sold to one of Pixels's well-known players.

                      <br/><br/>The loop goes something like this:
                      <br/>♾️Collect a Pop Chip from CyberBob (you do this every 4 hours - you have to click him once to collect your Pop Chip and once to get him to make another for you)
                      <br/>♾️Take the Pop Chip to the Van somewhere mid-town of Nucyber where they will scan the Pop Chip for Supplies (also every four hours and requires one click to get your boxes, and one click to scan a Pop Chip)
                      <br/>♾️Take your boxes to the Unboxing Station top right of NuCyber and unbox all the goodies - the drops are random
                      <br/>♾️Use the goodies to manufacture products (to turn on the manufacturing machine in the sewer, you need Fuel which is a random drop)
                      <br/>♾️When you have manufactured products, box them up using the Packaging Machine
                      <br/>♾️Sell Boxed/Packaged Good to the Store which is in the Bottom Left of NuCyber

                      <br/><br/>The loop is basic and can be completed as many times as you can/want`,
                },
                {
                  type: "table",
                  data: [
                    {
                      product: "Boots",
                      level: 0,
                      materials: "3 Leather<br/>2 Cloth",
                      exp: 80,
                    },
                    {
                      product: "Lighter",
                      level: 0,
                      materials: "3 Plastic<br/>1 Fuel",
                      exp: 50,
                    },
                    {
                      product: "Magnet",
                      level: 0,
                      materials: "4 Metal",
                      exp: 70,
                    },
                    {
                      product: "Flashlight",
                      level: 2,
                      materials: "2 Metal<br/>2 Plastic<br/>1 Wire",
                      exp: 120,
                    },
                    {
                      product: "Medkit",
                      level: 3,
                      materials: "2 Plastic<br/>5 Cloth<br/>2 Wire",
                      exp: 150,
                    },
                    {
                      product: "Jacket",
                      level: 5,
                      materials: "6 Leather<br/>2 Cloth",
                      exp: 220,
                    },
                    {
                      product: "VR",
                      level: 8,
                      materials: "6 Metal<br/>6 Plastic<br/>3 Wire",
                      exp: 350,
                    },
                    {
                      product: "Laptop",
                      level: 10,
                      materials: "8 Metal<br/>4 Plastic<br/>5 Wire",
                      exp: 500,
                    },
                  ],
                },
              ],
            },
          ],
        },{
          id: "exploration",
          title: "Exploration",
          type: "subsection",
          content: [
            {
              title: "Exploration",
              id: "about-exploration",
              type: "subdivision",
              content: [
                {
                  type: "text",
                  data: `Exploration currently includes Sushi Tables and Fishing! `,
                },{
                  type: "text",
                  data: `
                    Here are some tips about Fishing that might help you as you start your fishing journey:
                    <br/>🐟 To get a Fishing Rod, you need to buy them from the Seaside Stash Store for Buoy Bucks
                    <br/>🐟 You have a chance of missing the fishes if you don't click the correct amount of times
                    <br/>🐟 EXP is acquired when you get the fish, but the energy is taken when you cast the line

                    <br/><br/>🐟 You can fish in the Pier but the fish will be different from Speck, Land, Water and Space
                    <br/>🐟 Every land has a different kind of fish.  
                    <br/>🐟 Each click to reel in the fish takes 0.4 Energy`,
                },{
                  type: "text",
                  data: `
                    Here are some tips about Sushi Kits that might help you as you start your journey:
                  <br/>🍥You can use higher sushi benches even if you haven't reached the appropriate tier. So there's no limitation on which bench you can use

                  <br/><br/>🍥There's one public sushi bench on the Pier near Cod but it only has the beginner recipes
                  <br/>🍥 Sushi Kit Recipes can be acquired from the Seaside Stash Store for Buoy Bucks. The recipes are limited, so to craft higher recipes, you need to buy a new recipe each time.
                  <br/>🍥 You cannot place Sushi Kits inside the house`,
                },                 
              ],
            },
          ],
        },
      ],
    },
    {
      id: "levels",
      title: "Levels",
      type: "section",
      content: [
        {
          type: "component",
          data: "Levels",
        },
      ],
    },
  ],
};

export default skillsGuide;
